import React, { useState } from 'react';
import { Plus, Search, Edit2, Trash2, Loader2, Users, Clock, Tag, CheckCircle } from 'lucide-react';
import { useClasses, useCreateClass, useUpdateClass, useDeleteClass } from '../hooks/useClasses';
import { useClassTypes, useSubjects } from '../hooks/useClassSettings';
import { ClassForm } from '../components/classes';

const Classes = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingClass, setEditingClass] = useState(null);

  const { data, isLoading, error } = useClasses();
  const { data: classTypesData } = useClassTypes();
  const { data: subjectsData } = useSubjects();
  const createClass = useCreateClass();
  const updateClass = useUpdateClass();
  const deleteClass = useDeleteClass();

  const classes = data?.classes || [];
  const classTypes = classTypesData?.classTypes || [];
  const subjects = subjectsData?.subjects || [];
  
  const filteredClasses = classes.filter(c =>
    c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.classType?.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSubmit = async (formData) => {
    try {
      if (editingClass) {
        await updateClass.mutateAsync({ id: editingClass.id, data: formData });
      } else {
        await createClass.mutateAsync(formData);
      }
      setShowForm(false);
      setEditingClass(null);
    } catch (err) {
      console.error('Failed to save class:', err);
    }
  };

  const handleEdit = (classItem) => {
    setEditingClass(classItem);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this class?')) {
      try {
        await deleteClass.mutateAsync(id);
      } catch (err) {
        console.error('Failed to delete class:', err);
      }
    }
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingClass(null);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-purple-600" size={32} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-600">
        Failed to load classes: {error.message}
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Classes</h1>
          <p className="text-gray-500 mt-1">Manage your classes and programs with full customization.</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="mt-4 sm:mt-0 btn-primary flex items-center space-x-2"
        >
          <Plus size={20} />
          <span>Add Class</span>
        </button>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder="Search classes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="form-input pl-12"
          />
        </div>
      </div>

      {/* Classes Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredClasses.length === 0 ? (
          <div className="col-span-full text-center py-12 text-gray-500">
            No classes found
          </div>
        ) : (
          filteredClasses.map((classItem) => (
            <div key={classItem.id} className="bg-white rounded-xl p-6 shadow-sm border border-purple-100 card-hover">
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex flex-wrap gap-2">
                  <span className={`badge ${
                    classItem.status === 'active' ? 'badge-success' :
                    classItem.status === 'draft' ? 'badge-warning' :
                    'badge-secondary'
                  }`}>
                    {classItem.status}
                  </span>
                  {classItem.classType && (
                    <span 
                      className="badge"
                      style={{ 
                        backgroundColor: classItem.classType.color + '20',
                        color: classItem.classType.color,
                        border: `1px solid ${classItem.classType.color}`
                      }}
                    >
                      {classItem.classType.name}
                    </span>
                  )}
                </div>
                <div className="flex space-x-1">
                  <button 
                    onClick={() => handleEdit(classItem)}
                    className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                  >
                    <Edit2 size={18} />
                  </button>
                  <button 
                    onClick={() => handleDelete(classItem.id)}
                    className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>

              {/* Title */}
              <h3 className="text-xl font-bold text-gray-900 mb-2">{classItem.name}</h3>
              
              {/* Subjects */}
              {classItem.subjects && classItem.subjects.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-3">
                  {classItem.subjects.map(subject => (
                    <span 
                      key={subject.id}
                      className="text-xs px-2 py-0.5 rounded-full"
                      style={{ 
                        backgroundColor: subject.color + '20',
                        color: subject.color
                      }}
                    >
                      {subject.name}
                    </span>
                  ))}
                </div>
              )}

              {/* Enrollment Info */}
              <p className="text-sm text-gray-500 mb-4">
                {classItem.enrollmentCount || 0}/{classItem.capacity} students enrolled
              </p>

              {/* Schedule Preview */}
              <div className="space-y-2 mb-4">
                {classItem.scheduleDays && classItem.scheduleDays.length > 0 ? (
                  <div className="flex items-start text-sm text-gray-600">
                    <Clock size={16} className="mr-2 text-purple-500 mt-0.5" />
                    <div>
                      {classItem.scheduleDays.slice(0, 2).map((day, i) => (
                        <div key={i}>
                          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][day.dayOfWeek]} {day.startTime}-{day.endTime}
                        </div>
                      ))}
                      {classItem.scheduleDays.length > 2 && (
                        <div className="text-purple-600">
                          +{classItem.scheduleDays.length - 2} more days
                        </div>
                      )}
                    </div>
                  </div>
                ) : classItem.schedule ? (
                  <div className="flex items-center text-sm text-gray-600">
                    <Clock size={16} className="mr-2 text-purple-500" />
                    {Array.isArray(classItem.schedule) 
                      ? classItem.schedule.map(s => `${s.day} ${s.startTime}`).join(', ')
                      : classItem.schedule
                    }
                  </div>
                ) : null}

                {classItem.location && (
                  <div className="flex items-center text-sm text-gray-600">
                    <Tag size={16} className="mr-2 text-purple-500" />
                    {classItem.location}
                  </div>
                )}

                <div className="flex items-center text-sm text-gray-600">
                  <Users size={16} className="mr-2 text-purple-500" />
                  {classItem.availableSeats || classItem.capacity} seats available
                </div>

                {/* Payment Plans Preview */}
                {classItem.paymentPlans && classItem.paymentPlans.length > 0 && (
                  <div className="flex items-start text-sm text-gray-600">
                    <CheckCircle size={16} className="mr-2 text-purple-500 mt-0.5" />
                    <div>
                      {classItem.paymentPlans.filter(p => p.isActive).length} payment plan(s)
                    </div>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="pt-4 border-t border-purple-50 flex items-center justify-between">
                <span className="text-lg font-bold text-purple-600">
                  ${classItem.price || classItem.paymentPlans?.find(p => p.isDefault)?.amount || 0}
                  <span className="text-sm font-normal text-gray-500">/mo</span>
                </span>
                <button 
                  onClick={() => handleEdit(classItem)}
                  className="text-sm text-purple-600 hover:text-purple-700 font-medium"
                >
                  View Details →
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Class Form Modal */}
      {showForm && (
        <ClassForm
          classItem={editingClass}
          onSubmit={handleSubmit}
          onClose={handleCloseForm}
          classTypes={classTypes}
          subjects={subjects}
          availableClasses={classes}
          isLoading={createClass.isPending || updateClass.isPending}
        />
      )}
    </div>
  );
};

export default Classes;
